import { useQuery } from '@apollo/client';
import {
  ContentDeciderContainer,
  Generator,
  Loader,
  getTranslatedText,
  BackgroundIcon,
  withErrorBoundary
} from '@nile/components';
import {
  RMMClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import { isEditable } from '@nile/mangifera/src/Pebbles/Favourites';
import {
  getType as getPatchType,
  getAccessor as getPatchAccessor,
  columnsConfig
} from '@nile/rmm/src/modules/patch-management/services/patch-list-helper';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import gql from 'graphql-tag';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import sizeMe from 'react-sizeme';
import { Tag } from '@superops/ui-design-system';
import { getEmoji } from '@nile/mangifera/src/Pebbles/WidgetEmoji';
import { SVGIcon } from '@superops/super-icons';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions,
  Badge,
  NumberTitle,
  NumberCardContainer,
  DeleteIcon
} from '../HomeWidgetContainer';
import AccessDenied from './AccessDenied';
import DeletedView from './DeletedView';
import { useHistoricPatchStore } from '@nile/rmm/src/stores/historicPatchStore';

function PatchViewWidget(props) {
  const {
    widget,
    meta,
    user,
    intl,
    patchViews,
    size,
    deleteWidget,
    patchViewMeta,
    isAdvancedHierarchy
  } = props;

  const viewPermission = widget?.permission;
  const viewExist = patchViews.some((el) => el.id === widget.id);

  const context = useContext(HomeContext);
  const [loading, setLoading] = useState(!!widget && viewPermission);
  const [data, setData] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  // Get from Zustand store (using selector to prevent unnecessary re-renders)
  const fetchHistoryPatch = useHistoricPatchStore(
    (state) => state.isHistoricPatchesEnabled
  );

  const { configuredCols } = getProcessedConfig({
    entitiesMeta: meta,
    allColumns: patchViewMeta?.selectAttributes,
    defaultColumns: patchViewMeta?.defaultSelectedAttributes,
    intl,
    module: isAdvancedHierarchy ? 'AssetPatchDetail' : 'PatchDetail',
    columnPersonalization: null,
    searchAttributes: patchViewMeta?.searchAttributes
  });

  let currentView =
    viewPermission && viewExist
      ? patchViews.find((el) => el.id === widget.id)
      : widget;

  currentView = {
    ...currentView,
    module: 'PATCH',
    iconColor: 'black'
  };
  const options = {
    pageSize: 20,
    page: 1,
    view: { id: widget.id },
    sort: [
      {
        attribute: 'publishedDate',
        order: 'DESC'
      }
    ]
  };

  const navigateToView = () => {
    if (!viewPermission || !viewExist) return;
    const currentAppState = context?.userAppState || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState.appState);
    const tabIdx = updatedAppState.findIndex((el) => el.id === 'rmm-home');
    const tab = {
      id: 'rmm-home',
      content: getTabTemplate({ module: 'RMM_HOME' }),
      accessibilityLabel: 'RMM Home',
      module: 'RMM_HOME',
      tabId: 1,
      state: {},
      data: {},
      url: `/rmm/patch/${currentView.id}`
    };
    if (tabIdx === -1) {
      updatedAppState.push(tab);
    } else {
      updatedAppState[tabIdx] = tab;
    }
    context.updateUserAppState({
      ...currentAppState,
      appState: updatedAppState,
      activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
    });
    context.closeHomeOverlay();
    setTimeout(() => {
      context.switchTabFocus(tab);
    }, 200);
  };

  const getPatchListVariables = () => {
    const variables = {
      listInfo: {
        pageSize: 5,
        page: listInfo?.page,
        view: {
          id: props.id
        },
        type: 'ListInfoInput',
        required: true
      }
    };

    // Only include fetchHistoryPatch for getPatchListV2 (advanced hierarchy)
    if (isAdvancedHierarchy) {
      variables.fetchHistoryPatch = {
        type: 'Boolean',
        required: false,
        value: fetchHistoryPatch
      };
    }

    return variables;
  };

  const getFields = (filters) => {
    const patches = [
      'patchId',
      'patchKey',
      'title',
      'publishedDate',
      'category',
      'severity',
      'approvalStatus',
      'totalDeviceCount',
      'installedDeviceCount',
      'failedDeviceCount',
      {
        assetClass: ['classId']
      },
      {
        kbNumbers: ['kbNumber']
      },
      'failedPercent'
    ];
    const patchesv2 = [
      'patchId',
      'patchKey',
      'title',
      'publishedDate',
      'category',
      'severity',
      {
        kbNumbers: ['kbNumber']
      },
      'size',
      'version',
      'associatedAssets',
      {
        assetClass: ['classId']
      }
    ];

    const fieldsToRequest = [
      {
        listInfo: ['page', 'pageSize', 'hasMore', 'totalCount']
      },
      {
        patches: isAdvancedHierarchy ? patchesv2 : patches
      }
    ];
    return fieldsToRequest;
  };

  const getActions = useCallback((col) => {
    switch (col.name) {
      default:
        return {};
    }
  });

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const personalizedColumns = configuredCols;
    const generator = new Generator({
      operation: isAdvancedHierarchy ? 'getPatchListV2' : 'getPatchList',
      variables: getPatchListVariables(),
      fields: getFields(personalizedColumns)
    });
    const queryString = generator.queryBuilder();
    const queryVariables = {
      listInfo: pageOptions
        ? { ...pageOptions, view: { id: widget.id } }
        : options
    };

    // Only include fetchHistoryPatch for getPatchListV2 (advanced hierarchy)
    if (isAdvancedHierarchy) {
      queryVariables.fetchHistoryPatch = fetchHistoryPatch;
    }

    const response = await RMMClient.query({
      query: gql`
        ${queryString.query}
      `,
      variables: queryVariables,
      fetchPolicy: 'no-cache'
    });
    setLoading(false);
    const cols = personalizedColumns?.filter((el) => el);
    const allColumns = cols.map((col) => {
      return {
        Header: col?.label,
        accessor: getPatchAccessor(col),
        type: getPatchType(col),
        disableSortBy: true,
        disableFilters: true,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        actions: getActions(col),
        field: col
      };
      // columnGroup[0].columns.push(column);
    });
    setColumnGroup([{ Header: 'Name', columns: allColumns }]);
    const res = isAdvancedHierarchy
      ? response?.data?.getPatchListV2
      : response?.data?.getPatchList;
    setData(res?.patches);
    setListInfo(res?.listInfo);
  };

  useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user.userId,
      category: 'PatchView',
      name: widget.id
    },
    client: AccountClient,
    skip: !viewPermission,
    onCompleted(res) {
      setLoading(true);
      const personalizationJSON =
        res?.getPersonalizationByCategoryAndName?.value;
      fetchViewData(personalizationJSON);
    },
    onError(err) {
      setLoading(false);
      console.log(err);
    }
  });

  if (loading) {
    return (
      <WidgetWrapper>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      {size.width > 300 && size.height > 200 ? (
        <>
          <Header>
            <Title onClick={navigateToView}>
              <Badge className="mr15" color="#9fd6b24d">
                {getEmoji(currentView)}
              </Badge>
              {currentView?.name}
              {viewPermission && viewExist && (
                <div className="ml-10">
                  <Tag>
                    {data?.length}/{listInfo?.totalCount}
                  </Tag>
                </div>
              )}
            </Title>
            <div className="widget-header flex center fg1">
              <Actions className="widget-actions visibilityActions">
                <div onClick={deleteWidget.bind(this, widget)} className="ptr">
                  <SVGIcon name="Delete" />
                </div>
              </Actions>
            </div>
          </Header>
          {!viewPermission ? (
            <AccessDenied />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView />
              ) : (
                <Content>
                  <ContentDeciderContainer
                    isLoading={loading}
                    hasContent={!loading && data?.length > 0}
                    fullHeight
                    mainContent={
                      <TableView
                        noBulkSelect
                        context={context}
                        columns={columnGroup}
                        data={data}
                        setData={setData}
                        rowCount={20}
                        refreshTable={() => {}}
                        updateMyData={null}
                        defaultColumnCells={columnsConfig}
                        pagination={false}
                        pages={1}
                        activePage={0}
                        fullHeight
                      />
                    }
                    loadingContent={<Loader />}
                    newEmptyContainer
                    notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
                  />
                </Content>
              )}
            </>
          )}
        </>
      ) : (
        <NumberCardContainer>
          <Badge color="#9fd6b24d" className="icon">
            {getEmoji(currentView)}
          </Badge>
          <Actions className="icon visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
          {!viewPermission ? (
            <AccessDenied small />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView small />
              ) : (
                <div className="content ptr" onClick={navigateToView}>
                  <NumberTitle>{listInfo?.totalCount}</NumberTitle>
                  {currentView?.name}
                </div>
              )}
            </>
          )}
        </NumberCardContainer>
      )}
    </WidgetWrapper>
  );
}

export default withErrorBoundary(
  sizeMe({ monitorHeight: true })(PatchViewWidget)
);
